/**
 * Tagged-object transformation utilities for LLM schemas.
 *
 * LLM APIs struggle with discriminated unions because:
 * 1. z.literal() generates `const` instead of `enum` in JSON Schema
 * 2. Flattened discriminated unions have key collision issues
 *
 * The tagged-object format solves both by making the discriminator the key:
 * - Original: `{ type: 'kpi', value: 42 }`
 * - Tagged: `{ kpi: { value: 42 } }`
 *
 * This module provides three functions:
 * - `transformToTaggedObjects`: Transform JSON Schema output (outbound: app -> LLM)
 * - `extractDiscriminators`: Auto-detect discriminator keys from Zod schema
 * - `convertTaggedToDiscriminated`: Convert tagged back to original format (inbound: LLM -> app)
 */
import type { z } from 'zod';

/**
 * Internal definition structure of a Zod schema.
 * Provides typed access to Zod v4 internal `_def` properties.
 *
 * @internal
 */
interface ZodInternalDef {
  type?: string;
  values?: unknown[];
  /**
   * Zod v4 stores enum members under `entries` (Record<key, value>) when the
   * schema was constructed from a TypeScript-style object map, and under
   * `values` (array) when constructed from a string-array literal. Both shapes
   * appear in the wild; consumers that read enum values must handle either.
   */
  entries?: Record<string, unknown>;
  options?: z.ZodTypeAny[];
  element?: z.ZodTypeAny;
  innerType?: z.ZodTypeAny;
  schema?: z.ZodTypeAny;
  in?: z.ZodTypeAny;
}

/**
 * Internal Zod schema structure with `_def` property.
 *
 * @internal
 */
interface ZodSchemaWithDef {
  _def: ZodInternalDef;
}

/**
 * Type guard for Zod schemas with internal `_def` property.
 * Used to safely access Zod internals without `as any` assertions.
 */
function hasZodDef(schema: unknown): schema is ZodSchemaWithDef {
  return (
    typeof schema === 'object' &&
    schema !== null &&
    '_def' in schema &&
    typeof (schema as Record<string, unknown>)._def === 'object'
  );
}

/**
 * Internal Zod object schema structure with `shape` property.
 *
 * @internal
 */
interface ZodObjectWithShape {
  shape: Record<string, z.ZodTypeAny>;
}

/**
 * Type guard for Zod object schemas with `shape` property.
 */
function hasZodShape(schema: unknown): schema is ZodObjectWithShape {
  return (
    typeof schema === 'object' &&
    schema !== null &&
    'shape' in schema &&
    typeof (schema as Record<string, unknown>).shape === 'object'
  );
}

/**
 * JSON Schema types for transformation.
 * Uses loose typing since JSON Schema structure varies.
 */
type JsonSchema = Record<string, unknown>;
type JsonSchemaProperty = Record<string, unknown>;

/**
 * Options for JSON Schema transformation.
 */
export interface TransformOptions {
  /** The discriminator key in the original schema (default: 'type') */
  discriminatorKey?: string;
}

function extractConstValue(prop: JsonSchemaProperty): string | undefined {
  if ('const' in prop && typeof prop.const === 'string') {
    return prop.const;
  }
  if ('enum' in prop && Array.isArray(prop.enum) && prop.enum.length === 1 && typeof prop.enum[0] === 'string') {
    return prop.enum[0];
  }
  return undefined;
}

function getDiscriminatorValue(schema: JsonSchema, discriminatorKey: string): string | undefined {
  const properties = schema.properties as Record<string, JsonSchemaProperty> | undefined;
  if (!properties) {
    return undefined;
  }

  const discriminatorProp = properties[discriminatorKey];
  if (!discriminatorProp) {
    return undefined;
  }

  return extractConstValue(discriminatorProp);
}

function transformVariant(variant: JsonSchema, discriminatorKey: string, discriminatorValue: string): JsonSchema {
  const properties = variant.properties as Record<string, JsonSchemaProperty> | undefined;
  const required = variant.required as string[] | undefined;

  // Remove discriminator from properties
  const newProperties: Record<string, JsonSchemaProperty> = {};
  if (properties) {
    for (const key in properties) {
      if (key !== discriminatorKey && properties[key] !== undefined) {
        newProperties[key] = properties[key];
      }
    }
  }

  // Remove discriminator from required array
  const newRequired = required?.filter((r) => r !== discriminatorKey);

  // Build the inner schema (without discriminator)
  const innerSchema: JsonSchema = {
    type: 'object',
    properties: newProperties,
  };
  if (newRequired && newRequired.length > 0) {
    innerSchema.required = newRequired;
  }
  if ('additionalProperties' in variant) {
    innerSchema.additionalProperties = variant.additionalProperties;
  }

  // Wrap under discriminator value as key
  return {
    type: 'object',
    properties: {
      [discriminatorValue]: innerSchema,
    },
    required: [discriminatorValue],
    additionalProperties: false,
  };
}

/**
 * Recursively transforms JSON Schema, converting discriminated unions to tagged objects.
 *
 * Input anyOf:
 * ```json
 * { "anyOf": [{ "properties": { "type": { "const": "kpi" }, "value": {...} } }] }
 * ```
 *
 * Output anyOf (tagged format):
 * ```json
 * { "anyOf": [{ "properties": { "kpi": { "properties": { "value": {...} } } } }] }
 * ```
 *
 * @param jsonSchema - JSON Schema object from zodToJsonSchema
 * @param options - Transform options
 * @returns Transformed JSON Schema with tagged-object format
 */
export function transformToTaggedObjects(jsonSchema: JsonSchema, options: TransformOptions = {}): JsonSchema {
  const discriminatorKey = options.discriminatorKey ?? 'type';

  // Handle anyOf arrays (discriminated unions)
  if ('anyOf' in jsonSchema && Array.isArray(jsonSchema.anyOf)) {
    const variants = jsonSchema.anyOf as JsonSchema[];

    // Check if all variants have the discriminator pattern
    const discriminatorValues = variants.map((v) => getDiscriminatorValue(v, discriminatorKey));
    const allHaveDiscriminator = discriminatorValues.every((v) => v !== undefined);

    if (allHaveDiscriminator) {
      // Transform to tagged-object format - cast is safe because allHaveDiscriminator guarantees no undefined
      const taggedVariants = variants.map((variant, index) => {
        const discriminatorValue = discriminatorValues[index];
        if (discriminatorValue === undefined) {
          throw new Error(`Unexpected: discriminator value at index ${index} is undefined`);
        }
        return transformVariant(variant, discriminatorKey, discriminatorValue);
      });

      // Recursively transform the tagged variants to sanitize nested schemas
      // (strip propertyNames, additionalProperties: true/{}, etc.)
      return {
        anyOf: taggedVariants.map((v) => transformToTaggedObjects(v, options)),
      };
    }
  }

  // Recursively process nested schemas
  const result: JsonSchema = {};

  for (const key in jsonSchema) {
    const value = jsonSchema[key];

    // Skip propertyNames - not allowed by some API backends
    if (key === 'propertyNames') {
      continue;
    }

    if (key === 'properties' && typeof value === 'object' && value !== null) {
      // Recursively transform property schemas
      const properties = value as Record<string, JsonSchema | undefined>;
      const newProperties: Record<string, JsonSchema> = {};
      for (const propKey in properties) {
        const propValue = properties[propKey];
        if (propValue !== undefined) {
          newProperties[propKey] = transformToTaggedObjects(propValue, options);
        }
      }
      result[key] = newProperties;
      continue;
    }

    if (key === 'items' && typeof value === 'object' && value !== null) {
      // Recursively transform array item schema
      result[key] = transformToTaggedObjects(value as JsonSchema, options);
      continue;
    }

    if (key === 'additionalProperties') {
      // Handle additionalProperties for API compatibility
      // - Strip true and empty objects {} (z.record/z.looseObject patterns)
      // - Preserve false (strict object validation)
      // - Preserve valid schema objects with type/anyOf
      // JSON Schema defaults to allowing any properties without explicit additionalProperties
      if (value === true) {
        // Skip - true is the default behavior anyway
        continue;
      }
      if (typeof value === 'object' && value !== null) {
        const additionalProps = value as JsonSchema;
        if (Object.keys(additionalProps).length === 0) {
          // Skip empty object - same as true (allows any properties)
          continue;
        }
        // Valid schema object - transform and keep it
        result[key] = transformToTaggedObjects(additionalProps, options);
        continue;
      }
      // Keep false or other values
      result[key] = value;
      continue;
    }

    if (key === 'anyOf' || key === 'oneOf' || key === 'allOf') {
      // Already handled anyOf above for discriminated unions
      // For other cases, recursively transform
      if (Array.isArray(value)) {
        result[key] = value.map((v: JsonSchema) => transformToTaggedObjects(v, options));
      } else {
        result[key] = value;
      }
      continue;
    }

    result[key] = value;
  }

  return result;
}

function extractLiteralValue(schema: z.ZodTypeAny): string | undefined {
  if (!hasZodDef(schema)) {
    return undefined;
  }

  // Zod v4 $ZodTypeDef intersects with ZodInternalDef, losing custom properties
  const def = schema._def as ZodInternalDef;

  // z.literal('x') — historical literal form.
  if (def.type === 'literal' && Array.isArray(def.values) && def.values.length > 0) {
    const value = def.values[0];
    return typeof value === 'string' ? value : undefined;
  }

  // z.enum(['x']) with exactly one member — used as a literal stand-in
  // throughout this codebase because z.literal() is banned by the schema
  // design rule. Mirrors `extractConstValue`'s JSON-Schema handling so the
  // inbound Zod-side discriminator detection stays in sync with the outbound
  // JSON-Schema-side tagging.
  if (def.type === 'enum') {
    if (Array.isArray(def.values) && def.values.length === 1 && typeof def.values[0] === 'string') {
      return def.values[0];
    }
    if (def.entries) {
      const entryValues = Object.values(def.entries);
      if (entryValues.length === 1 && typeof entryValues[0] === 'string') {
        return entryValues[0];
      }
    }
  }

  return undefined;
}

function extractUnionOptions(schema: z.ZodTypeAny): z.ZodTypeAny[] {
  if (!hasZodDef(schema)) {
    return [];
  }

  // Zod v4 $ZodTypeDef intersects with ZodInternalDef, losing custom properties
  const def = schema._def as ZodInternalDef;
  if (def.type === 'union' && Array.isArray(def.options)) {
    return def.options;
  }

  return [];
}

// Safely extracts shape from Zod object schema, returns undefined if not an object schema.
function getZodObjectShape(schema: z.ZodTypeAny): Record<string, z.ZodTypeAny> | undefined {
  if (hasZodShape(schema)) {
    return schema.shape;
  }
  return undefined;
}

/**
 * Finds the discriminator key field that has a literal value in ALL union branches.
 * Returns the field name (e.g., 'type' or 'kind') or undefined if no common discriminator exists.
 */
function findDiscriminatorKey(unionOptions: z.ZodTypeAny[]): string | undefined {
  const shapes = unionOptions.map(getZodObjectShape);

  // All union options must be object schemas
  if (shapes.some((s) => s === undefined)) {
    return undefined;
  }

  const firstShape = shapes[0];
  if (!firstShape) {
    return undefined;
  }

  // Check each field in the first shape to see if it's a literal in ALL branches
  for (const field of Object.keys(firstShape)) {
    const allHaveLiteral = shapes.every((shape) => {
      if (!shape) {
        return false;
      }
      const fieldSchema = shape[field];
      if (!fieldSchema) {
        return false;
      }
      return extractLiteralValue(fieldSchema) !== undefined;
    });

    if (allHaveLiteral) {
      return field;
    }
  }

  return undefined;
}

function extractDiscriminatorsRecursive(
  schema: z.ZodTypeAny,
  result: Map<string, string>,
  visited: WeakSet<object>,
): void {
  if (visited.has(schema)) {
    return;
  }
  visited.add(schema);

  if (!hasZodDef(schema)) {
    return;
  }

  // Zod v4 $ZodTypeDef intersects with ZodInternalDef, losing custom properties
  const def = schema._def as ZodInternalDef;

  // Check if this is a union with a discriminator
  const unionOptions = extractUnionOptions(schema);
  if (unionOptions.length > 0) {
    const key = findDiscriminatorKey(unionOptions);
    if (key) {
      for (const option of unionOptions) {
        const shape = getZodObjectShape(option);
        if (shape) {
          const fieldSchema = shape[key];
          if (fieldSchema) {
            const value = extractLiteralValue(fieldSchema);
            if (value) {
              result.set(value, key);
            }
          }
        }
      }
    }

    // Recurse into union options for nested discriminated unions
    for (const option of unionOptions) {
      extractDiscriminatorsRecursive(option, result, visited);
    }
    return;
  }

  // Object: recurse into property schemas
  const shape = getZodObjectShape(schema);
  if (shape) {
    for (const propSchema of Object.values(shape)) {
      if (propSchema) {
        extractDiscriminatorsRecursive(propSchema, result, visited);
      }
    }
    return;
  }

  // Array: recurse into element
  if (def.type === 'array' && def.element) {
    extractDiscriminatorsRecursive(def.element, result, visited);
    return;
  }

  // Wrapped types (optional, nullable, default, etc.)
  if (def.innerType) {
    extractDiscriminatorsRecursive(def.innerType, result, visited);
    return;
  }

  // Zod v4: wrapped schema
  if (def.schema) {
    extractDiscriminatorsRecursive(def.schema, result, visited);
    return;
  }

  // Zod v4: pipe schemas (created by .transform(), .pipe(), etc.)
  if (def.in) {
    extractDiscriminatorsRecursive(def.in, result, visited);
    return;
  }
}

/**
 * Auto-detects discriminator keys from a Zod schema and returns a Map
 * from discriminator value to discriminator key name.
 *
 * Walks the Zod schema tree recursively, finding unions where all branches
 * share a common field with literal values. The discriminator key is detected
 * automatically (not hardcoded to 'type').
 *
 * @param schema - Zod schema to extract discriminators from
 * @returns Map from discriminator value to its key name (e.g., Map { 'kpi' -> 'type' })
 */
export function extractDiscriminators(schema: z.ZodTypeAny): Map<string, string> {
  const result = new Map<string, string>();
  extractDiscriminatorsRecursive(schema, result, new WeakSet());
  return result;
}

/**
 * Recursively converts tagged-object format to discriminated union format.
 *
 * LLM APIs output tagged format: `{ chart: { chartType: 'line', ... } }`
 * Tools expect discriminated format: `{ type: 'chart', chartType: 'line', ... }`
 *
 * A single-key object is tagged if its key is in the discriminators Map.
 * The Map provides the discriminator field name for each value
 * (e.g., `'kpi' -> 'type'` converts `{ kpi: {...} }` to `{ type: 'kpi', ... }`).
 *
 * @param value - Value to process (may contain tagged objects anywhere in the tree)
 * @param discriminators - Map from discriminator value to key name, from `extractDiscriminators()`
 * @returns Value with tagged objects converted to discriminated format
 */
// FIXME: generic return type is technically a lie -- the function transforms data shape at runtime.
// Pragmatic for consumer/test ergonomics until we have proper type-safe conversion.
export function convertTaggedToDiscriminated<T = unknown>(value: T, discriminators: Map<string, string>): T {
  if (value === null || value === undefined) {
    return value;
  }
  if (typeof value !== 'object') {
    return value;
  }

  // Arrays: recursively process each element
  if (Array.isArray(value)) {
    return value.map((item) => convertTaggedToDiscriminated(item, discriminators)) as T;
  }

  // Objects: check if tagged, then process
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj);

  // Find a key that matches a known discriminator value.
  // Strict case: single-key object like { kpi: { value: 42 } }
  // Relaxed case: multi-key object where exactly one key is a discriminator value
  //   e.g. { chart: { chartType: "line" }, caption: "..." } — LLMs sometimes
  //   nest sibling properties inside the tagged object.
  const taggedEntry = keys.flatMap((k) => {
    const discriminatorKey = discriminators.get(k);
    return discriminatorKey === undefined ? [] : [[k, discriminatorKey] as const];
  })[0];

  if (taggedEntry) {
    const [taggedKey, discriminatorKey] = taggedEntry;
    const inner = obj[taggedKey];
    if (typeof inner === 'object' && inner !== null && !Array.isArray(inner)) {
      // Convert: { chart: { chartType: "line" }, caption: "..." }
      //       -> { type: "chart", chartType: "line", caption: "..." }
      const { [taggedKey]: _, ...extraKeys } = obj;
      const converted = {
        [discriminatorKey]: taggedKey,
        ...(inner as Record<string, unknown>),
        ...extraKeys,
      };
      // Recurse into the converted object in case inner values also have tagged objects
      return convertTaggedToDiscriminated(converted, discriminators) as T;
    }
  }

  // Not a tagged object: recursively process all property values
  const result: Record<string, unknown> = {};
  for (const [key, propValue] of Object.entries(obj)) {
    result[key] = convertTaggedToDiscriminated(propValue, discriminators);
  }
  return result as T;
}
